// /markt/polymarket — Polymarket Prediction-Markets als interaktives Tool.
//
// Phase-2: Search + Tag-Filter + Sort + Frag-EVE-Button.
// Backend (api/src/crons/fetchers.ts fetchPolymarket) liefert seit Phase-2
// 30 Märkte mit tags/liquidity/endDate/description.

import { useEffect, useMemo, useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import {
  ArrowUpDown,
  Calendar,
  Droplet,
  ExternalLink,
  Search,
  Sparkles,
} from 'lucide-react';
import { selectFullData, useMarketStore } from '@/stores/market-store';
import { useEveDrawerStore } from '@/stores/eve-drawer-store';
import { useEvePageContextStore } from '@/stores/eve-page-context-store';
import { PolymarketDetailModal } from '@/components/markets/polymarket-detail-modal';
import { buildPolymarketPrompt } from '@/lib/polymarket-api';

interface PolymarketMarketRaw {
  id?: string;
  /** Polymarket condition_id — Schlüssel für /prices-history. Manche Crons
   *  speichern es separat, manche als `id`. Wir fallback'en zwischen beiden. */
  conditionId?: string;
  question?: string;
  slug?: string;
  description?: string;
  outcomes?: string[];
  outcomePrices?: string | unknown[];
  /** CLOB Token-IDs für Bid/Ask-Lookup. Pro Outcome eine ID, kommt als
   *  JSON-String aus dem Polymarket Gamma-API. */
  clobTokenIds?: string | unknown[];
  volume24hr?: number;
  volumeTotal?: number;
  liquidity?: number;
  endDate?: string;
  tags?: Array<{ id?: string; label?: string; slug?: string }>;
}

type SortKey = 'volume24h' | 'liquidity' | 'endDate' | 'edge';

const SORT_OPTIONS: ReadonlyArray<{ key: SortKey; label: string }> = [
  { key: 'volume24h', label: 'Volumen 24h' },
  { key: 'liquidity', label: 'Liquidität' },
  { key: 'endDate', label: 'Ende bald' },
  { key: 'edge', label: 'Edge (Quote nah 50%)' },
];

const TAG_LABELS_DE: Record<string, string> = {
  politics: 'Politik',
  crypto: 'Crypto',
  geopolitics: 'Geo',
  trump: 'Trump',
  'trump-iran': 'Trump Iran',
  'fed-rates': 'Rate Cuts',
  fed: 'Fed',
  fomc: 'FOMC',
  'jerome-powell': 'Powell',
  economy: 'Wirtschaft',
  tech: 'Tech',
  culture: 'Kultur',
  business: 'Business',
  elections: 'Wahlen',
};

/**
 * Fest definierte Tag-Filter in dieser Reihenfolge (Doc 2026-05-15).
 * Jeder Filter kann ein oder mehrere slugs aggregieren (z.B. „Rate Cuts" =
 * fed-rates + fomc + jerome-powell). Filter-Match: Markt hat MINDESTENS einen
 * der slugs.
 */
interface TagFilter {
  /** Eindeutiger Filter-Key (auch state-value). */
  key: string;
  label: string;
  /** Welche Polymarket-Tag-Slugs zählen für diesen Filter. */
  slugs: ReadonlyArray<string>;
}

const FIXED_TAGS: ReadonlyArray<TagFilter> = [
  { key: 'crypto', label: 'Crypto', slugs: ['crypto', 'crypto-prices', 'bitcoin'] },
  { key: 'geopolitics', label: 'Geo', slugs: ['geopolitics', 'macro-geopolitics', 'world'] },
  { key: 'politics', label: 'Politik', slugs: ['politics'] },
  { key: 'trump', label: 'Trump', slugs: ['trump'] },
  { key: 'trump-iran', label: 'Trump Iran', slugs: ['trump-iran', 'iran', 'israel-x-iran'] },
  { key: 'fed-rates', label: 'Rate Cuts', slugs: ['fed-rates', 'fomc', 'jerome-powell'] },
  { key: 'fed', label: 'Fed', slugs: ['fed'] },
];

export function PolymarketPage() {
  const fullData = useMarketStore(selectFullData);
  const askEve = useEveDrawerStore((s) => s.askEve);
  const openDrawer = useEveDrawerStore((s) => s.open);
  const setPageContext = useEvePageContextStore((s) => s.setPageContext);
  const clearPageContext = useEvePageContextStore((s) => s.clearPageContext);
  const reduceMotion = useReducedMotion();
  const [researchingId, setResearchingId] = useState<string | null>(null);
  const [detailMarket, setDetailMarket] = useState<PolymarketMarketRaw | null>(null);

  const allMarkets = (fullData?.polymarket ?? []) as PolymarketMarketRaw[];

  const [search, setSearch] = useState('');
  const [activeTag, setActiveTag] = useState<string>('all');
  const [sortKey, setSortKey] = useState<SortKey>('volume24h');

  // Tag-Counts für die FIXED_TAGS. Multi-Slug-Match: Markt wird gezählt
  // wenn er MINDESTENS einen der Filter-Slugs hat.
  const tagCounts = useMemo(() => {
    return FIXED_TAGS.map((tag) => {
      const slugSet = new Set(tag.slugs);
      const count = allMarkets.filter((m) =>
        (m.tags ?? []).some((t) => slugSet.has(t.slug ?? '')),
      ).length;
      return { ...tag, count };
    });
  }, [allMarkets]);

  // Filter + Sort
  const filtered = useMemo(() => {
    let list = [...allMarkets];

    // Tag-Filter (Multi-Slug-Match)
    if (activeTag !== 'all') {
      const tagDef = FIXED_TAGS.find((t) => t.key === activeTag);
      if (tagDef) {
        const slugSet = new Set(tagDef.slugs);
        list = list.filter((m) =>
          (m.tags ?? []).some((t) => slugSet.has(t.slug ?? '')),
        );
      }
    }

    // Search
    const q = search.trim().toLowerCase();
    if (q.length > 0) {
      list = list.filter((m) => (m.question ?? '').toLowerCase().includes(q));
    }

    // Sort
    list.sort((a, b) => {
      switch (sortKey) {
        case 'liquidity':
          return (b.liquidity ?? 0) - (a.liquidity ?? 0);
        case 'endDate': {
          const aEnd = a.endDate ? Date.parse(a.endDate) : Infinity;
          const bEnd = b.endDate ? Date.parse(b.endDate) : Infinity;
          return aEnd - bEnd;
        }
        case 'edge': {
          const edgeOf = (m: PolymarketMarketRaw) => {
            const { yesPct } = parseOutcomePrices(m.outcomePrices);
            if (yesPct == null) return 100;
            return Math.abs(yesPct - 50); // niedriger = näher 50% = mehr Edge
          };
          return edgeOf(a) - edgeOf(b);
        }
        case 'volume24h':
        default:
          return (b.volume24hr ?? 0) - (a.volume24hr ?? 0);
      }
    });

    return list;
  }, [allMarkets, activeTag, search, sortKey]);

  const handleAskEve = (market: PolymarketMarketRaw) => {
    const id = market.id ?? market.question ?? '';
    if (researchingId) return;
    setResearchingId(id);
    openDrawer();

    // Cost-Optimization 2026-05-19: keine "recherchiere selbst"-Prompts mehr.
    // Wir geben EVE die vorhandenen Markt-Daten direkt mit. Routing landet bei
    // EVE-Polymarket-Take (Haiku 4.5, kein Web-Fetch). Frueher: 100k+ Tokens
    // pro Frage durch Anthropic Web-Fetch — jetzt ~2k.
    const { yesPct, noPct } = parseOutcomePrices(market.outcomePrices);
    const prompt = buildPolymarketPrompt({
      question: market.question ?? '',
      outcomes: [
        { label: 'YES', pct: yesPct },
        { label: 'NO', pct: noPct },
      ],
      volume24h: market.volume24hr ?? null,
      tags: (market.tags ?? [])
        .map((t) => t.label)
        .filter((l): l is string => typeof l === 'string' && l.length > 0)
        .slice(0, 5),
    });
    askEve(prompt, { displayText: market.question ?? '' });
    setResearchingId(null);
  };

  // Page-Context für EVE setzen — sie weiß damit dass sie auf Polymarket ist
  // und welche Märkte/Filter Doc gerade sieht.
  useEffect(() => {
    const visibleMarkets = allMarkets.slice(0, 10).map((m) => {
      const { yesPct, noPct } = parseOutcomePrices(m.outcomePrices);
      return {
        question: m.question,
        yes: yesPct != null ? `${yesPct.toFixed(1)}%` : '—',
        no: noPct != null ? `${noPct.toFixed(1)}%` : '—',
        volume24h: m.volume24hr,
        tags: (m.tags ?? []).map((t) => t.label).slice(0, 3),
      };
    });
    setPageContext({
      pageKey: '/markt/polymarket',
      title: 'Polymarket',
      summary: `Polymarket-Übersicht mit ${allMarkets.length} aktiven Märkten nach 24h-Volumen. Aktiver Filter: ${activeTag === 'all' ? 'alle Tags' : activeTag}. Sortierung: ${sortKey}.`,
      data: { topMarkets: visibleMarkets, activeTag, sortKey, totalCount: allMarkets.length },
    });
    return () => clearPageContext();
  }, [allMarkets, activeTag, sortKey, setPageContext, clearPageContext]);

  return (
    <div className="relative h-full overflow-y-auto">
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -left-32 top-12 size-96 rounded-full bg-amber-500/[0.06] blur-3xl" />
        <div className="absolute right-12 top-1/3 size-80 rounded-full bg-emerald-500/[0.04] blur-3xl" />
      </div>

      <div className="relative mx-auto max-w-5xl space-y-5 px-6 py-8">
        {/* Header */}
        <motion.header
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ type: 'spring', stiffness: 280, damping: 28 }}
          className="space-y-2 border-b border-white/5 pb-4"
        >
          <div className="flex items-baseline gap-3">
            <h1 className="bg-gradient-to-r from-amber-200 via-amber-100 to-emerald-200 bg-clip-text text-3xl font-bold tracking-tight text-transparent">
              Polymarket
            </h1>
            <span className="text-[11px] uppercase tracking-widest text-neutral-600">
              {allMarkets.length} Märkte · Crowd-Odds · Live 60s
            </span>
          </div>
          <p className="max-w-2xl text-sm leading-relaxed text-neutral-400">
            Wo die Crowd mit echtem Geld auf Zukunft wettet. Edge liegt nicht wo
            alle hinschauen — sondern wo die Quoten von der Realität abweichen.
          </p>
        </motion.header>

        {/* Toolbar: Search + Sort */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
          className="flex flex-col gap-3 sm:flex-row sm:items-center"
        >
          <div className="relative flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-neutral-500" />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Suche Märkte …"
              className="w-full rounded-lg border border-white/10 bg-neutral-900/60 py-2 pl-9 pr-3 text-sm text-neutral-100 placeholder:text-neutral-500 outline-none transition-colors focus:border-amber-500/40 focus:bg-neutral-900/80"
            />
          </div>
          <div className="flex items-center gap-2">
            <ArrowUpDown className="size-3.5 text-neutral-500" />
            <select
              value={sortKey}
              onChange={(e) => setSortKey(e.target.value as SortKey)}
              className="cursor-pointer rounded-lg border border-white/10 bg-neutral-900/60 px-3 py-2 text-sm text-neutral-200 outline-none transition-colors hover:bg-neutral-900/80 focus:border-amber-500/40"
            >
              {SORT_OPTIONS.map((o) => (
                <option key={o.key} value={o.key}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
        </motion.div>

        {/* Tag-Filter-Chips — feste Reihenfolge, kein dynamisches Top-N */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="flex flex-wrap gap-2"
        >
          <TagChip
            label="Alle"
            count={allMarkets.length}
            active={activeTag === 'all'}
            onClick={() => setActiveTag('all')}
          />
          {tagCounts.map((tag) => (
            <TagChip
              key={tag.key}
              label={tag.label}
              count={tag.count}
              active={activeTag === tag.key}
              onClick={() => setActiveTag(tag.key)}
            />
          ))}
        </motion.div>

        {/* Markets-Grid: 2-spaltig auf md+, 1-spaltig mobil */}
        {filtered.length === 0 ? (
          <div className="rounded-xl border border-dashed border-white/10 bg-neutral-950/40 p-8 text-center text-sm text-neutral-500">
            {allMarkets.length === 0
              ? 'Keine Märkte geladen — warte auf den nächsten Cron-Tick.'
              : `Keine Treffer für „${search}"${activeTag !== 'all' ? ` in ${activeTag}` : ''}.`}
          </div>
        ) : (
          <motion.div
            initial={reduceMotion ? 'visible' : 'hidden'}
            animate="visible"
            variants={{
              hidden: {},
              visible: { transition: { staggerChildren: 0.04 } },
            }}
            className="grid gap-4 md:grid-cols-2"
          >
            {filtered.map((m, i) => {
              const id = m.id ?? m.question ?? '';
              return (
                <PolymarketMarketRow
                  key={id || i}
                  market={m}
                  isResearching={researchingId === id}
                  onAskEve={() => handleAskEve(m)}
                  onPreview={() => setDetailMarket(m)}
                />
              );
            })}
          </motion.div>
        )}
      </div>

      {detailMarket ? (
        <PolymarketDetailModal
          question={detailMarket.question ?? '—'}
          slug={detailMarket.slug ?? null}
          outcomes={buildOutcomeSpecs(detailMarket)}
          onClose={() => setDetailMarket(null)}
        />
      ) : null}
    </div>
  );
}

function buildOutcomeSpecs(market: PolymarketMarketRaw): Array<{ label: string; conditionId: string; tokenId?: string | null }> {
  const conditionId = market.conditionId ?? market.id ?? '';
  if (!conditionId) return [];
  const labels: string[] = Array.isArray(market.outcomes) && market.outcomes.length > 0
    ? market.outcomes
    : ['Yes', 'No'];
  const tokenIds = (() => {
    const v = market.clobTokenIds;
    if (!v) return null;
    if (Array.isArray(v)) return v.map(String);
    if (typeof v === 'string') {
      try {
        const parsed = JSON.parse(v) as unknown;
        if (Array.isArray(parsed)) return parsed.map(String);
      } catch {
        return null;
      }
    }
    return null;
  })();
  return labels.map((label, i) => ({
    label,
    conditionId,
    tokenId: tokenIds?.[i] ?? null,
  }));
}

// ── Tag-Chip ──────────────────────────────────────────────────────────────

interface TagChipProps {
  label: string;
  count: number;
  active: boolean;
  onClick: () => void;
}

function TagChip({ label, count, active, onClick }: TagChipProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`group flex items-center gap-1.5 rounded-full border px-3 py-1 text-[12px] font-medium transition-all ${
        active
          ? 'border-amber-400/50 bg-amber-500/15 text-amber-200 shadow-[0_0_12px_-2px_rgba(251,191,36,0.4)]'
          : 'border-white/10 bg-neutral-900/40 text-neutral-400 hover:border-white/20 hover:bg-neutral-900/70 hover:text-neutral-200'
      }`}
    >
      {label}
      <span
        className={`tabular text-[10px] ${active ? 'text-amber-300/70' : 'text-neutral-500 group-hover:text-neutral-400'}`}
      >
        {count}
      </span>
    </button>
  );
}

// ── Market-Row ────────────────────────────────────────────────────────────

interface PolymarketMarketRowProps {
  market: PolymarketMarketRaw;
  isResearching?: boolean;
  onAskEve: () => void;
  onPreview: () => void;
}

const rowVariants = {
  hidden: { opacity: 0, y: 10, scale: 0.98, filter: 'blur(3px)' },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    filter: 'blur(0px)',
    transition: { type: 'spring' as const, stiffness: 300, damping: 28, mass: 0.7 },
  },
};


function PolymarketMarketRow({ market, isResearching, onAskEve, onPreview }: PolymarketMarketRowProps) {
  const reduceMotion = useReducedMotion();
  const { yesPct } = parseOutcomePrices(market.outcomePrices);
  const yesPctSafe = yesPct ?? 0;
  const isExtreme = yesPctSafe < 5 || yesPctSafe > 95;
  const edgeColor = isExtreme
    ? 'border-neutral-700/40'
    : Math.abs(yesPctSafe - 50) < 20
      ? 'border-amber-500/30 hover:border-amber-400/60'
      : 'border-white/10 hover:border-white/20';

  const endDateInfo = formatEndDate(market.endDate);
  const topTag = market.tags?.[0];

  return (
    <motion.article
      variants={rowVariants}
      whileHover={reduceMotion ? undefined : { y: -3, scale: 1.01 }}
      transition={{ type: 'spring', stiffness: 350, damping: 25 }}
      onClick={(e) => {
        // Body-Klick öffnet Preview-Modal — Buttons/Links eskalieren nicht
        // hierher (stopPropagation auf den interaktiven Elementen).
        if ((e.target as HTMLElement).closest('button, a')) return;
        onPreview();
      }}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onPreview();
        }
      }}
      className={`group relative flex cursor-pointer flex-col overflow-hidden rounded-2xl border bg-gradient-to-br from-neutral-900/80 via-neutral-950/70 to-neutral-900/80 p-4 backdrop-blur-sm transition-colors ${edgeColor}`}
    >
      {/* Shine on hover */}
      <div className="pointer-events-none absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/[0.04] to-transparent transition-transform duration-700 group-hover:translate-x-full" />

      {/* Top-Row: Tag-Badge + ExternalLink + Frag-EVE */}
      <div className="relative mb-2 flex items-start justify-between gap-2">
        <div className="flex flex-wrap items-center gap-1.5">
          {topTag?.label ? (
            <span className="rounded-md border border-amber-500/30 bg-amber-500/10 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-amber-300">
              {TAG_LABELS_DE[topTag.slug ?? ''] ?? topTag.label}
            </span>
          ) : null}
          {market.tags && market.tags.length > 1 ? (
            <span className="text-[10px] text-neutral-600">+{market.tags.length - 1}</span>
          ) : null}
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <button
            type="button"
            onClick={onAskEve}
            disabled={isResearching}
            className="group/btn flex items-center gap-1 rounded-md border border-amber-500/30 bg-amber-500/10 px-2 py-1 text-[11px] font-medium text-amber-200 transition-all hover:border-amber-400/60 hover:bg-amber-500/20 hover:text-amber-100 disabled:opacity-60 disabled:cursor-wait"
            title={isResearching ? 'Recherche läuft…' : 'Frag EVE — mit echter Web-Recherche'}
          >
            <Sparkles
              className={`size-3 transition-transform group-hover/btn:rotate-12 ${
                isResearching ? 'animate-pulse' : ''
              }`}
            />
            {isResearching ? 'Recherche…' : 'Frag EVE'}
          </button>
          {market.slug ? (
            <a
              href={`https://polymarket.com/event/${market.slug}`}
              target="_blank"
              rel="noopener noreferrer"
              className="rounded-md p-1 text-neutral-500 transition-all hover:rotate-12 hover:bg-white/5 hover:text-amber-300"
              aria-label="Open on Polymarket"
            >
              <ExternalLink className="size-3.5" />
            </a>
          ) : null}
        </div>
      </div>

      {/* Question */}
      <h3 className="relative mb-3 line-clamp-2 min-h-[2.5rem] text-[13.5px] font-medium leading-snug text-neutral-100">
        {market.question}
      </h3>

      {/* Speedometer-Gauge */}
      <div className="relative mb-3 flex flex-1 items-center justify-center py-1">
        <YesNoGauge yesPct={yesPctSafe} animate={!reduceMotion} />
      </div>

      {/* Bottom-Row: Liquidity + EndDate + Volume */}
      <div className="relative flex flex-wrap items-center justify-between gap-2 border-t border-white/5 pt-2.5 text-[11px] tabular text-neutral-500">
        {market.liquidity != null ? (
          <span className="flex items-center gap-1" title="Liquidität">
            <Droplet className="size-3" />
            {fmtMoney(market.liquidity)}
          </span>
        ) : (
          <span />
        )}
        {endDateInfo ? (
          <span
            className={`flex items-center gap-1 ${endDateInfo.urgent ? 'text-amber-400' : ''}`}
            title={`Resolved: ${market.endDate}`}
          >
            <Calendar className="size-3" />
            {endDateInfo.label}
          </span>
        ) : null}
        <span title="24h Volumen">Vol {fmtMoney(market.volume24hr ?? 0)}</span>
      </div>
    </motion.article>
  );
}

// ── Speedometer-Gauge ─────────────────────────────────────────────────────
// Halbkreis-Tachometer: SVG-Arc von 180° (links, NO) bis 0° (rechts, YES).
// Animated stroke-dashoffset + Color-gradient (rot → amber → grün).

interface YesNoGaugeProps {
  yesPct: number;
  animate: boolean;
}

function YesNoGauge({ yesPct, animate }: YesNoGaugeProps) {
  const clamped = Math.max(0, Math.min(100, yesPct));
  // Halbkreis: cx=100, cy=80, r=70. Arc-length = π*r ≈ 219.9
  const cx = 100;
  const cy = 80;
  const r = 70;
  const arcLength = Math.PI * r;
  // Wie viel des Arcs ist YES?
  const yesArcLength = (clamped / 100) * arcLength;

  // Farb-Wahl basierend auf YES% (rot bei 0%, amber bei 50%, grün bei 100%)
  const arcColor =
    clamped < 25
      ? '#f43f5e' // rose-500
      : clamped < 45
        ? '#fb923c' // orange-400
        : clamped < 55
          ? '#fbbf24' // amber-400
          : clamped < 75
            ? '#a3e635' // lime-400
            : '#10b981'; // emerald-500

  // Nadel-Winkel: 180° (links = 0% YES) bis 0° (rechts = 100% YES).
  // SVG y-Axis ist invertiert, also wir nutzen rotate.
  const needleAngle = 180 - (clamped / 100) * 180;
  const needleLength = r - 8;
  const needleX = cx + Math.cos((needleAngle * Math.PI) / 180) * needleLength;
  const needleY = cy - Math.sin((needleAngle * Math.PI) / 180) * needleLength;

  return (
    <div className="relative w-full">
      <svg viewBox="0 0 200 110" className="w-full max-w-[200px] mx-auto block">
        <defs>
          <linearGradient id="gauge-track" x1="0%" x2="100%" y1="0%" y2="0%">
            <stop offset="0%" stopColor="#f43f5e" stopOpacity="0.25" />
            <stop offset="50%" stopColor="#fbbf24" stopOpacity="0.25" />
            <stop offset="100%" stopColor="#10b981" stopOpacity="0.25" />
          </linearGradient>
        </defs>

        {/* Background-Track (full half-circle, faint gradient) */}
        <path
          d={`M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${cx + r} ${cy}`}
          fill="none"
          stroke="url(#gauge-track)"
          strokeWidth="14"
          strokeLinecap="round"
        />

        {/* Active arc (YES%) */}
        <motion.path
          d={`M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${cx + r} ${cy}`}
          fill="none"
          stroke={arcColor}
          strokeWidth="14"
          strokeLinecap="round"
          strokeDasharray={arcLength}
          initial={animate ? { strokeDashoffset: arcLength } : { strokeDashoffset: arcLength - yesArcLength }}
          animate={{ strokeDashoffset: arcLength - yesArcLength }}
          transition={{ type: 'spring', stiffness: 80, damping: 18, delay: 0.15 }}
          style={{ filter: `drop-shadow(0 0 8px ${arcColor}80)` }}
        />

        {/* Tick-Marks bei 0/25/50/75/100 */}
        {[0, 25, 50, 75, 100].map((tick) => {
          const angle = 180 - (tick / 100) * 180;
          const x1 = cx + Math.cos((angle * Math.PI) / 180) * (r - 14);
          const y1 = cy - Math.sin((angle * Math.PI) / 180) * (r - 14);
          const x2 = cx + Math.cos((angle * Math.PI) / 180) * (r - 22);
          const y2 = cy - Math.sin((angle * Math.PI) / 180) * (r - 22);
          return (
            <line
              key={tick}
              x1={x1}
              y1={y1}
              x2={x2}
              y2={y2}
              stroke="rgba(255,255,255,0.15)"
              strokeWidth="1.5"
            />
          );
        })}

        {/* Needle */}
        <motion.line
          x1={cx}
          y1={cy}
          x2={needleX}
          y2={needleY}
          stroke={arcColor}
          strokeWidth="2.5"
          strokeLinecap="round"
          initial={animate ? { pathLength: 0, opacity: 0 } : { pathLength: 1, opacity: 1 }}
          animate={{ pathLength: 1, opacity: 1 }}
          transition={{ delay: 0.5, duration: 0.4 }}
          style={{ filter: `drop-shadow(0 0 4px ${arcColor})` }}
        />

        {/* Needle pivot */}
        <circle cx={cx} cy={cy} r="5" fill={arcColor} />
        <circle cx={cx} cy={cy} r="2.5" fill="#0a0a0a" />
      </svg>

      {/* Center-Text: YES% groß + Labels */}
      <div className="absolute inset-0 flex flex-col items-center justify-end pb-1">
        <div className="text-[10px] uppercase tracking-wider text-neutral-500">YES</div>
        <div
          className="text-3xl font-bold tabular leading-none"
          style={{ color: arcColor }}
        >
          {clamped.toFixed(1)}%
        </div>
      </div>

      {/* Edge-Labels NO/YES links/rechts */}
      <div className="mt-1 flex justify-between text-[9px] uppercase tracking-wider text-neutral-600">
        <span>NO 100%</span>
        <span>50/50</span>
        <span>YES 100%</span>
      </div>
    </div>
  );
}

// ── Helpers ───────────────────────────────────────────────────────────────

function parseOutcomePrices(raw: string | unknown[] | undefined): {
  yesPct: number | null;
  noPct: number | null;
} {
  if (!raw) return { yesPct: null, noPct: null };
  let arr: unknown[] | null = null;
  if (Array.isArray(raw)) arr = raw;
  else if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw) as unknown;
      if (Array.isArray(parsed)) arr = parsed;
    } catch {
      return { yesPct: null, noPct: null };
    }
  }
  if (!arr || arr.length < 2) return { yesPct: null, noPct: null };
  const yes = Number.parseFloat(String(arr[0])) * 100;
  const no = Number.parseFloat(String(arr[1])) * 100;
  return {
    yesPct: Number.isFinite(yes) ? yes : null,
    noPct: Number.isFinite(no) ? no : null,
  };
}

function fmtMoney(v: number): string {
  if (!Number.isFinite(v) || v === 0) return '—';
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000) return `$${(v / 1_000).toFixed(0)}k`;
  return `$${v.toFixed(0)}`;
}

function formatEndDate(endDate?: string): { label: string; urgent: boolean } | null {
  if (!endDate) return null;
  const ts = Date.parse(endDate);
  if (!Number.isFinite(ts)) return null;
  const diffMs = ts - Date.now();
  if (diffMs <= 0) return { label: 'beendet', urgent: false };
  const days = Math.floor(diffMs / (24 * 60 * 60 * 1000));
  const hours = Math.floor(diffMs / (60 * 60 * 1000));
  if (days === 0) return { label: `in ${hours}h`, urgent: hours < 24 };
  if (days === 1) return { label: 'morgen', urgent: true };
  if (days < 7) return { label: `in ${days}d`, urgent: true };
  if (days < 30) return { label: `in ${days}d`, urgent: false };
  const months = Math.floor(days / 30);
  return { label: `in ${months}M`, urgent: false };
}

